<template>
  <div class="login-component">
    <form v-on:submit.prevent.stop="doLogin">
      <div class="form-group mb-3">
        <label class="form-label text-muted small fw-bold text-uppercase">Email Address</label>
        <input 
          type="email" 
          class="form-control tc-login-input" 
          id="email" 
          v-model="attributes.email"
          placeholder="name@company.com" 
          :disabled="loading"
          required
        >
      </div>

      <div class="form-group mb-4">
        <label class="form-label text-muted small fw-bold text-uppercase">Password</label>
        <input 
          type="password" 
          class="form-control tc-login-input" 
          id="password" 
          v-model="attributes.password"
          placeholder="Enter your password" 
          :disabled="loading"
          required
        >
      </div>

      <button type="submit" class="btn tc-login-btn-primary-gradient w-100 rounded-pill py-2 fw-bold mb-3" :disabled="loading">
        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
        Login <i class="fa fa-arrow-right ms-2" v-if="!loading"></i>
      </button>
    </form>

    <div class="position-relative my-4 text-center">
      <hr class="text-muted opacity-25" />
      <span class="bg-white px-3 text-muted small position-absolute top-50 start-50 translate-middle" style="z-index: 1;">OR</span>
    </div>

    <!-- Microsoft Login -->
    <b-link 
      class="btn btn-outline-dark w-100 rounded-pill py-2 fw-medium d-flex align-items-center justify-content-center" 
      :href="`${config.buildUrl('api', '/api/connect/microsoft')}?scope=openid profile email Mail.Send`">
      <b-spinner small v-if="loading" :disabled="loading" class="me-2"/> 
      <i class="fab fa-microsoft me-2" style="color: #00a4ef;"></i> Login with Microsoft
    </b-link>

    <div class="mt-3">
      <p class="tc-login-error text-center">{{ errorMessage }}</p>
    </div>

    <div class="mt-2 text-center pt-4 border-top mt-4">
      <p class="text-muted mb-0">Don't have an account?
        <button @click="toggleForm" class="btn btn-link tc-login-text-primary fw-bold p-0 ms-1 text-decoration-none">
          Sign Up
        </button>
      </p>
    </div>
  </div>
</template>

<script>
import { inject } from "vue";

export default {
  data() {
    return {
      loading: false,
      attributes: {
        email: '',
        password: '',
      },
      errorMessage: ''
    };
  },
  setup() {
    const { profile, loginPw, loginJwt } = inject('user');
    const config = inject('config');
    return { profile, loginPw, loginJwt, config };
  },
  methods: {
    async doLogin() {
      this.loading = true;
      try {
        await this.loginPw(this.attributes.email, this.attributes.password);
        if (!this.profile.role) {
          this.profile.role = {};
        }

        const userProfileWithRole = {
          id: this.profile.id,
          username: this.profile.username,
          email: this.profile.email,
          role: this.profile.role,
        };

        // Store authentication token and user profile
        localStorage.setItem('authToken', this.loginJwtToken);
        localStorage.setItem('userProfile', JSON.stringify(userProfileWithRole));

        this.$bvToast.toast(`Welcome: ${this.profile.email}`, {
          title: 'Login successful',
          variant: 'success',
          solid: true,
        });

        this.$router.push({ name: 'home' });
      } catch (error) {
        this.handleAuthError(error);
      } finally {
        this.loading = false;
      }
    },

    handleAuthError(error) {
      this.errorMessage = error.response?.data?.error?.message || "Something went wrong";
      this.$bvToast.toast(this.errorMessage, {
        title: "Error",
        variant: "danger",
        solid: true,
      });
    },

    toggleForm() {
      this.$router.push({ name: "signup" });
    },
  },

  async mounted() {
    const url = new URL(window.location.toString().replace('/#', ''));

    if (url.searchParams.has('access_token')) {
      this.loading = true;

      try {
        const accessToken = url.searchParams.get('access_token');
        const res = await this.$xhr.get(`/api/auth/microsoft/callback?access_token=${accessToken}`);
        
        // Store the access token directly from the URL
        localStorage.setItem('authToken', accessToken);

        // Ensure the JWT is stored correctly
        await this.loginJwt(res.data.jwt);
        
        const userProfileWithRole = {
          id: this.profile.id,
          username: this.profile.username,
          email: this.profile.email,
          role: this.profile.role,
        };

        // Store user profile in localStorage
        localStorage.setItem('userProfile', JSON.stringify(userProfileWithRole));

        this.$bvToast.toast(`Welcome: ${this.profile.email}`, {
          title: 'Login successful',
          variant: 'success',
          solid: true,
        });

        this.$router.push({ name: 'home' });
      } catch (e) {
        console.log("MS Login error", e);
        this.handleAuthError(e);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.tc-login-input {
  background-color: #F8F9FC;
  border: 1px solid #E2E2EA;
  border-radius: 12px;
  padding: 0.8rem 1.2rem;
  font-size: 1rem;
  color: #11142D;
  transition: all 0.2s;
}
.tc-login-input:focus {
  border-color: #6C5DD3;
  box-shadow: 0 0 0 0.25rem rgba(108, 93, 211, 0.15);
  background-color: #ffffff;
}

.tc-login-btn-primary-gradient {
  background: #6C5DD3;
  border: none;
  color: white;
  transition: transform 0.2s, box-shadow 0.2s;
}
.tc-login-btn-primary-gradient:hover:not(:disabled) {
  transform: translateY(-2px);
  background: #5b4eb8;
  box-shadow: 0 6px 20px rgba(108, 93, 211, 0.3);
  color: white;
}

.tc-login-text-primary {
  color: #6C5DD3 !important;
}

.tc-login-error {
  color: #FF754C;
  font-size: 0.9rem;
  font-weight: 500;
}
</style>
